import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaMapMarkerAlt,
  FaUserFriends,
  FaHandHoldingUsd,
  FaCoins,
} from 'react-icons/fa';
import { Button } from '../../components';
import logo from '../../assets/klyx-logo1.png';
import fanSupportFlowSvg from '../../assets/diagrams/fan-support-flow.svg';
import profileResaleSvg from '../../assets/diagrams/profile-resale.svg';
import './Fans.scss';

const FansPage: React.FC = () => {
  const navigate = useNavigate();

  // Land at the top on route in — same pattern as sibling marketing
  // pages (/about, /how-it-works, /creators).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="fans-page">
      <div className="fans-container">
        {/* Hero — fan-only pitch. Refactor doc §6.1. */}
        <header className="fans-header">
          <img src={logo} alt="Klyx" className="fans-header__logo" />
          <h1>Back the creators worth your attention.</h1>
          <p className="fans-header__tagline">
            Find the ones building good work before the algorithm does.
            Follow, support, and — if you&apos;re feeling early — own their
            profile. When they earn, you earn a cut.
          </p>
          <div className="fans-header__cta">
            <Button variant="primary" size="large" onClick={() => navigate('/leaderboards')}>
              Explore creators →
            </Button>
          </div>
        </header>

        {/* §6.2 — What you can do as a fan. Four tiles. The
            "Own their profile, earn with them" tile is the
            differentiated hook — the SFT trading mechanic reframed
            as fan alignment, not speculation. */}
        <section className="fans-section">
          <h2>What you can do as a fan</h2>
          <div className="fans-tiles">
            <div className="fans-tile">
              <FaMapMarkerAlt className="fans-tile__icon" aria-hidden />
              <h3>Discover locally</h3>
              <p>
                See who&apos;s building near you. Reputation-ranked
                discovery surfaces the real work, not the biggest
                account.
              </p>
            </div>
            <div className="fans-tile">
              <FaUserFriends className="fans-tile__icon" aria-hidden />
              <h3>Follow directly</h3>
              <p>
                No algorithm choosing what you see. You pick the
                creators worth your attention and their posts land in
                your feed, no filter.
              </p>
            </div>
            <div className="fans-tile">
              <FaHandHoldingUsd className="fans-tile__icon" aria-hidden />
              <h3>Support them however you want</h3>
              <p>
                Buy their paid posts. Subscribe to their subscribers-only
                content. Boost them with a spotlight. 95% of everything
                you pay goes straight to the creator.
              </p>
            </div>
            <div className="fans-tile">
              <FaCoins className="fans-tile__icon" aria-hidden />
              <h3>Own their profile, earn with them</h3>
              <p>
                Buy a creator&apos;s profile SFT and earn 3% of every paid
                post or subscription they sell — forever. If someone
                else buys the profile from you later, you take the
                resale profit and the creator still earns their 3%
                royalty on top.
              </p>
            </div>
          </div>
        </section>

        {/* §6.3 — Why "own the profile" is different. */}
        <section className="fans-section">
          <h2>Why &ldquo;own the profile&rdquo; is different</h2>
          <p>
            This isn&apos;t &ldquo;invest in a person.&rdquo; It&apos;s a fan
            alignment mechanic. You back your favourite creator early;
            they blow up on the platform; their PPV posts and
            subscribers pay you a cut alongside them. If you ever want
            to hand the profile off to a bigger fan, the creator earns
            their royalty on that resale too — you don&apos;t take from
            them by leaving.
          </p>
          <p>
            Profiles are always for sale. Anyone can buy an owned profile
            by paying 10% above its current price — no negotiation, no
            gatekeeping. That guarantees liquidity for fans who want out,
            and the 10% minimum guarantees a positive return every time
            the profile changes hands. Full mechanics on{' '}
            <a href="/how-it-works">how it works</a>.
          </p>
          <figure className="fans-diagram">
            <img src={profileResaleSvg} alt="Profile resale flow: Fan A buys creator's profile SFT and earns 3% of the creator's ongoing sales forever. Fan B later pays +10% and the sale settles automatically. Resale proceeds split 95% to Fan A (profit), 3% to the original creator (royalty forever), 2% to Klyx platform. Fan B becomes the new owner and starts earning 3%." />
            <figcaption>Profile resale — how ownership passes hands</figcaption>
          </figure>
        </section>

        {/* §6.4 — How your money reaches the creator. Diagram
            replaces the text-based 3-step flow that shipped in
            PR 5 as a placeholder for the diagram-tooling PR. */}
        <section className="fans-section">
          <h2>How your money reaches the creator</h2>
          <p>
            When you tap subscribe or buy a PPV post, the payment settles
            on-chain directly to the creator&apos;s Klever wallet. No middleman
            deciding whether or when they get paid. 95% goes straight to the
            creator; the other 5% covers a 3% profile-owner cut and a 2%
            platform fee.
          </p>
          <figure className="fans-diagram">
            <img src={fanSupportFlowSvg} alt="Fan support flow: fan taps subscribe or buy PPV → Klyx app builds the transaction → fan signs with Klever wallet → Klever blockchain settles the payment → creator receives 95%, profile owner receives 3%, Klyx platform receives 2%" />
            <figcaption>Fan support flow — how the money moves</figcaption>
          </figure>
        </section>

        {/* §6.5 CTA */}
        <section className="fans-cta">
          <h2>Join and start following.</h2>
          <p>Free to sign up. You only pay creators when you want to.</p>
          <div className="fans-cta__buttons">
            <Button
              variant="primary"
              size="large"
              onClick={() => navigate('/signup?intent=fan')}
            >
              Sign up and start following →
            </Button>
            <Button variant="ghost" size="large" onClick={() => navigate('/leaderboards')}>
              Browse creators first
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FansPage;
